import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BorrowingBaseEnabledGuard } from '@core/guards/borrowing-base-enabled/borrowing-base-enabled.guard';
import { ClientService, IsoService, SearchService } from '@core/services';
import { NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { OiqLayoutComponent } from '@oiq-layout/layout.component';
import { OiqLayoutService } from '@oiq-layout/layout.service';
import { OiqNotificationEventToast } from '@oiq-notification/events/events-toast.component';
import { OiqNotificationEventsService } from '@oiq-notification/events/events.service';
import { OiqNotificationEventsStore } from '@oiq-notification/events/events.store';
import { OIQ_FORM_DEFAULT_ERRORS, OIQ_FORM_ERROR_MAP } from '@oiq-ui-sdk/form';
import {
  OiqUiAlertDialogService,
  OiqUiDialogService,
  OiqUiNotificationService,
} from '@oiq-ui-sdk/ui';
import { MatPaginatorCustom } from '@shared/custom-paginator';
import { OiqApplicationEventService } from '@v2/modules/applications/components/event-dialog/event-dialog.component';
import { COLLECTION_FILTER_SEARCH_REGISTRY } from '@v2shared/components/collection-table/filters/filter-search.component';
import { COLLECTION_FILTER_COMPONENT_REGISTRY } from '@v2shared/components/collection-table/filters/filter.models';
import { COLLECTION_FILTER_COMPONENT_REGISTRY_MAP } from '@v2shared/components/collection-table/filters/filter.registry';
import { CookieService } from 'ngx-cookie-service';
import { provideEnvironmentNgxCurrency } from 'ngx-currency';
import { DROPZONE_CONFIG, DropzoneConfigInterface, DropzoneModule } from 'ngx-dropzone-wrapper';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { ToastrModule } from 'ngx-toastr';
import { APP_SVG_LIBRARY_PROVIDER } from 'src/icons/app.icons';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppService } from './app.service';
import { BBReportsComponent } from './borrowing-base/bb-reports/bb-reports.component';
import { BorrowingBaseMainComponent } from './borrowing-base/borrowing-base-main.component';
import { ExemptionDialogComponent } from './borrowing-base/exemption-dialog/exemption-dialog.component';
import { BorrowingBaseComponent } from './borrowing-base/view/borrowing-base.component';
import { AuthGuard } from './core/authentication/auth.guard';
import { AuthService } from './core/authentication/auth.service';
import { CoreModule } from './core/core.module';
import { HttpIntercept } from './core/http.interceptor';
import { LoginComponent } from './login/login.component';
import { MaterialModule } from './material';
import { PlaidLinkComponentComponent } from './plaid-link-component/plaid-link-component.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { CalendarEventToast } from './shared/navigation/calendar-events/calendar-event-toast/calendar-events-toast.component';
import { SharedModule } from './shared/shared.module';
import { SmsMessagingDialogComponent } from './sms-messaging/sms-messaging-dialog/sms-messaging-dialog.component';
import { SmsMessagingComponent } from './sms-messaging/sms-messaging.component';
import { EMAIL_APP_STORE_PROVIDER } from './v2/modules/email-app/email-app.store';
import { V2Module } from './v2/v2.module';

const DEFAULT_DROPZONE_CONFIG: DropzoneConfigInterface = {
  url: 'api/attachments/upload',
  maxFilesize: 50,
  timeout: 0,
};

export const customCurrencyMaskConfig = {
  align: 'right',
  allowNegative: true,
  allowZero: true,
  decimal: '.',
  precision: 2,
  prefix: '$',
  suffix: '',
  thousands: ',',
  nullable: true,
};

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    ResetPasswordComponent,
    BorrowingBaseMainComponent,
    BorrowingBaseComponent,
    BBReportsComponent,
    ExemptionDialogComponent,
    CalendarEventToast,
    PlaidLinkComponentComponent,
    SmsMessagingDialogComponent,
    SmsMessagingComponent,
  ],
  imports: [
    BrowserModule,
    // HttpClientModule,
    ReactiveFormsModule,
    AppRoutingModule,
    CoreModule,
    BrowserAnimationsModule,
    MaterialModule,
    DropzoneModule,
    SharedModule,
    NgxSliderModule,
    NgbTypeaheadModule,
    FormsModule,
    CommonModule,
    MatMenuModule,
    ToastrModule.forRoot({
      toastComponent: OiqNotificationEventToast, // added custom toast!
      preventDuplicates: true,
    }),
    V2Module,
    OiqLayoutComponent,
  ],
  providers: [
    AuthGuard,
    Title,
    AuthService,
    BorrowingBaseEnabledGuard,
    CookieService,
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: DROPZONE_CONFIG,
      useValue: DEFAULT_DROPZONE_CONFIG,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpIntercept,
      multi: true,
    },
    {
      provide: MatPaginatorIntl,
      useClass: MatPaginatorCustom,
    },
    AppService,
    OiqLayoutService,
    OiqUiAlertDialogService,
    OiqUiDialogService,
    OiqUiNotificationService,
    OiqUiAlertDialogService,
    OiqNotificationEventsService,
    OiqNotificationEventsStore,
    OiqApplicationEventService,
    EMAIL_APP_STORE_PROVIDER,
    provideEnvironmentNgxMask({}),
    provideEnvironmentNgxCurrency(customCurrencyMaskConfig),
    {
      provide: OIQ_FORM_ERROR_MAP,
      useValue: {
        ...OIQ_FORM_DEFAULT_ERRORS,
        greaterThanBuyRate: 'Can not be greater than Buy Rate',
        lessThanFactorRate: 'Can not be less than Factor Rate',
        // The SDK date validator raises these three, but ships no copy for
        // them, and the error renderer falls back to printing the raw key.
        // Generic on purpose — a field that can say something more useful
        // (which date, and why) overrides them with its own [errorMap].
        invalidDate: 'Date is not valid',
        minDate: 'Date is earlier than allowed',
        maxDate: 'Date is later than allowed',
      },
    },
    {
      provide: COLLECTION_FILTER_COMPONENT_REGISTRY,
      useValue: COLLECTION_FILTER_COMPONENT_REGISTRY_MAP,
    },
    {
      provide: COLLECTION_FILTER_SEARCH_REGISTRY,
      useFactory: (_search: SearchService, _isos: IsoService, _clients: ClientService) => ({
        // performClientsTypeahead: {
        //   search: (term: string, definition: CollectionFilterDefinition) =>
        //     _search.performClientsTypeahead(term),
        // },
        // performIsosTypeahead: (term: string, definition: CollectionFilterDefinition) =>
        //   _search.performIsosTypeahead(term),
      }),
      deps: [SearchService, ClientService, IsoService],
    },
    APP_SVG_LIBRARY_PROVIDER,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
